import axios from 'axios';

const API_BASE_URL = 'https://api.trip-trot.com/api';
// const API_BASE_URL = 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/v1/auth/login', credentials),
  register: (userData) => api.post('/v1/auth/register', userData),
  getProfile: () => api.get('/v1/auth/me'),
  updateProfile: (userData) => api.put('/v1/auth/me', userData),
  changePassword: (passwordData) => api.put('/v1/auth/password', passwordData),
  verifyEmail: (token) => api.get(`/v1/auth/verify-email?token=${token}`),
  resendVerification: (email) => api.post('/v1/auth/resend-verification', { email }),
  forgotPassword: (email) => api.post('/v1/auth/forgot-password', { email }),
  validateResetToken: (token) => api.get(`/v1/auth/validate-reset-token?token=${token}`),
  resetPassword: (token, newPassword) => api.post('/v1/auth/reset-password', { token, newPassword }),
  setupTwoFactor: () => api.post('/v1/auth/2fa/setup'),
  enableTwoFactor: (code) => api.post('/v1/auth/2fa/enable', { verificationCode: code }),
  verifyTwoFactor: (code) => api.post('/v1/auth/2fa/verify', { verificationCode: code }),
  verifyTwoFactorLogin: (tempToken, verificationCode) => api.post('/v1/auth/2fa/login-verify', { tempToken, verificationCode }),
  disableTwoFactor: (password) => api.post('/v1/auth/2fa/disable', { password }),
  submitVerification: (documents) => api.post('/v1/verification/submit', documents, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getLoginHistory: (limit = 10) => api.get('/v1/auth/login-history', { params: { limit } }),
  getVerificationStatus: () => api.get('/v1/verification/status'),
};

// Parcels API
export const parcelsAPI = {
  createParcel: (parcelData) => api.post('/v1/parcels', parcelData),
  getAvailableParcels: (filters) => api.get('/v1/parcels/public', { params: filters }),
  getUserParcels: (status, params = {}) => api.get('/v1/parcels/my', { params: { status, ...params } }),
  getParcelDetails: (id) => api.get(`/v1/parcels/${id}`),
  updateParcel: (id, parcelData) => api.put(`/v1/parcels/${id}`, parcelData),
  deleteParcel: (id) => api.delete(`/v1/parcels/delete/${id}`),
  cancelParcel: (id) => api.put(`/v1/parcels/cancel/${id}`),
  getMatchingTrips: (id) => api.get(`/v1/parcels/${id}/matching-trips`),
  getParcelApplications: (id) => api.get(`/v1/parcels/${id}/applications`),
};

// Trips API
export const tripsAPI = {
  createTrip: (tripData) => api.post('/v1/trips', tripData),
  getAvailableTrips: (filters) => {
    console.log(filters);
    return api.get('/v1/trips/available', { params: filters })
  },
  getUserTrips: (status, params = {}) => api.get('/v1/trips/my', { params: { status, ...params } }),
  // getPostedTrips: () => api.get('/v1/trips/posted'),
  // getCancelledTrips: () => api.get('/v1/trips/cancelled'),
  getTripDetails: (id) => api.get(`/v1/trips/${id}`),
  updateTrip: (id, tripData) => api.put(`/v1/trips/${id}`, tripData),
  cancelTrip: (id) => api.put(`/v1/trips/cancel/${id}`),
  deleteTrip: (id) => api.delete(`/v1/trips/delete/${id}`),
  getTripApplications: (id) => api.get(`/v1/trips/${id}/applications`),
  getTripAgreements: (id) => api.get(`/v1/trips/${id}/agreements`),
};

// Applications API
export const applicationsAPI = {
  createApplication: (data) => api.post('/v1/applications', data),
  getUserApplications: (params = {}) => api.get('/v1/applications/my', { params }),
  getReceivedApplications: (params = {}) => api.get('/v1/applications/received', { params }),
  getApplicationDetails: (id) => api.get(`/v1/applications/${id}`),
  cancelApplication: (id) => api.delete(`/v1/applications/${id}`),
  acceptApplication: (id) => api.put(`/v1/applications/${id}/accept`),
  rejectApplication: (id, reason) => api.put(`/v1/applications/${id}/reject`, { reason }),
  deleteApplication: (id) => api.delete(`/v1/applications/${id}/delete`),
};

// Agreements API (Production endpoints with idempotency)
export const agreementsAPI = {
  acceptApplication: (applicationId) => api.post('/v1/agreements/accept', { application_id: applicationId }),
  confirmPickup: ({ id: agreementId, data }) => {
    console.log('confirmPickup called with:', agreementId, data);
    
    if (data instanceof FormData) {
      return api.post(`/v1/agreements/${agreementId}/pickup`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post(`/v1/agreements/${agreementId}/pickup`, data);
  },
  markDelivered: ({ id: agreementId, data }) => {
    if (data instanceof FormData) {
      return api.post(`/v1/agreements/${agreementId}/delivered`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return api.post(`/v1/agreements/${agreementId}/delivered`, data);
  },
  confirmDelivery: ({ id: agreementId, data }) => {
    console.log('confirmDelivery called with:', agreementId, data);
    
    if (data instanceof FormData) {
      return api.post(`/v1/agreements/${agreementId}/receiver-confirm`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 seconds for fund processing
      });
    }
    return api.post(`/v1/agreements/${agreementId}/receiver-confirm`, data, {
      timeout: 60000 // 60 seconds for fund processing
    });
  },
  cancelAgreement: (agreementId, reason) => 
    api.post(`/v1/agreements/${agreementId}/cancel`, { reason }),
  getAgreementDetails: (agreementId) => api.get(`/v1/agreements/${agreementId}`),
  getUserAgreements: (params) => {
    return api.get('/v1/agreements/my', { params });
  },
  generateReceipt: (agreementId) => {
    return api.get(`/v1/agreements/${agreementId}/receipt`, {
      responseType: 'blob' // Important for PDF download
    });
  },
};

// Wallet API (Production endpoints)
export const walletAPI = {
  createWallet: () => api.post('/v1/wallet/create'),
  getWallet: () => api.get('/v1/wallet'),
  getTransactionHistory: (limit, offset) => 
    api.get('/v1/wallet/transactions', { params: { limit, offset } }),
  createFundingRequest: (data) => api.post('/v1/wallet/fund-request', data),
  lockFunds: (data) => api.post('/v1/wallet/lock', data),
  releaseFunds: (lockId) => api.post('/v1/wallet/release', { lock_id: lockId }),
  forfeitFunds: (lockId) => api.post('/v1/wallet/forfeit', { lock_id: lockId }),
  getLocks: () => api.get('/v1/wallet/locks'),
  getLockStats: () => api.get('/v1/wallet/locks/stats'),
};

// Disputes API (Production endpoints with evidence)
export const disputesAPI = {
  createDispute: (data, files) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => formData.append(key, data[key]));
    if (files) {
      files.forEach(file => formData.append('evidence_files', file));
    }
    return api.post('/v1/disputes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getDispute: (id) => api.get(`/v1/disputes/${id}`),
  getUserDisputes: () => api.get('/v1/disputes/my'),
  getAgreementDisputes: (agreementId) => api.get(`/v1/disputes/agreement/${agreementId}`),
  addEvidence: (id, files) => {
    const formData = new FormData();
    files.forEach(file => formData.append('evidence_files', file));
    return api.post(`/v1/disputes/${id}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Reputation API
export const reputationAPI = {
  getReputation: () => api.get('/v1/reputation'),
  getReputationEvents: () => api.get('/v1/reputation/events'),
  getReputationStats: () => api.get('/v1/reputation/stats'),
};

// Fees API
export const feesAPI = {
  getFees: () => api.get('/v1/fees'),
  calculateFeeEstimate: (actionType, data) => 
    api.get('/v1/fees/estimate', { params: { action: actionType, ...data } }),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/v1/admin/dashboard'),
  getUsers: (filters) => api.get('/v1/admin/users', { params: filters }),
  verifyUser: (userId, status) => api.put(`/v1/admin/users/${userId}/verify`, { status }),
  getFees: () => api.get('/v1/admin/fees'),
  updateFee: (feeType, feeData) => api.put(`/v1/admin/fees/${feeType}`, feeData),
  getSettings: () => api.get('/v1/admin/settings'),
  updateSetting: (key, value) => api.put(`/v1/admin/settings/${key}`, { value }),
  getFundingRequests: () => api.get('/v1/admin/funding-requests'),
  approveFundingRequest: (requestId) => api.put(`/v1/admin/transactions/${requestId}/approve`),
  getDisputes: () => api.get('/v1/admin/disputes'),
  resolveDispute: (id, data) => api.put(`/v1/admin/disputes/${id}/resolve`, data),
};

// Dashboard API
export const dashboardAPI = {
  getDashboardData: () => api.get('/v1/dashboard'),
};

export default api;
